/**
 * 独享容器「应用」页的工具卡片：计划任务 / 后台服务 / 部署 / PHP 设置。
 * 计划任务与后台服务在容器内执行（节点代理按时 exec），定义整体保存。
 */
import React, { useRef, useState } from 'react';
import {
    Button, Card, Form, Input, InputNumber, Popconfirm, Select, Space, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { DeleteOutlined, PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { containerUserApi as api, isOk, errMsg } from '../../../shared/services/containerService';
import { arr } from '../../../shared/components/container/ContainerParts';

const { Text } = Typography;
const mono = { fontFamily: 'monospace' };
const fmtTime = (ts) => (ts ? new Date(Number(ts) * 1000).toLocaleString() : '');

const useRows = (list) => {
    const seq = useRef(1);
    const [rows, setRows] = useState(() => arr(list).map((x) => { seq.current += 1; return { ...x, rid: seq.current }; }));
    const set = (rid, k, v) => setRows((rs) => rs.map((r) => (r.rid === rid ? { ...r, [k]: v } : r)));
    const add = (row) => { seq.current += 1; setRows((rs) => [...rs, { ...row, rid: seq.current }]); };
    const del = (rid) => setRows((rs) => rs.filter((r) => r.rid !== rid));
    return [rows, set, add, del];
};

const strip = (rows) => rows.map(({ rid, ...r }) => r);

// ------------------------------------------------------------------ 计划任务

export const CronCard = ({ c, reload }) => {
    const { t } = useTranslation();
    const saved = arr(c.apps?.cron);
    const [rows, set, add, del] = useRows(saved);
    const [saving, setSaving] = useState(false);
    const status = c.apps_status?.cron || {};
    const savedIds = new Set(saved.map((j) => j.id));

    const save = async () => {
        setSaving(true);
        const r = await api.apps(c.id, { cron: strip(rows), services: arr(c.apps?.services) });
        setSaving(false);
        if (isOk(r)) { message.success(t('Saved')); reload(); } else message.error(errMsg(r, t('Failed to save')));
    };
    const run = async (id) => {
        const r = await api.runCron(c.id, id);
        if (isOk(r)) message.success(t('Started')); else message.error(errMsg(r, t('Operation failed')));
    };

    const cols = [
        { title: '', key: 'en', width: 56, render: (_, r) => <Switch size="small" checked={r.enabled !== false} onChange={(v) => set(r.rid, 'enabled', v)} /> },
        {
            title: <Tooltip title={t('minute hour day month weekday, e.g. */5 * * * *; or @hourly @daily')}>{t('Time')}</Tooltip>, key: 's', width: 150,
            render: (_, r) => <Input size="small" style={mono} value={r.schedule} placeholder="*/5 * * * *" onChange={(e) => set(r.rid, 'schedule', e.target.value)} />,
        },
        { title: t('Command'), key: 'c', render: (_, r) => <Input size="small" style={mono} value={r.command} onChange={(e) => set(r.rid, 'command', e.target.value)} /> },
        {
            title: t('Last run'), key: 'st', width: 130, render: (_, r) => {
                const s = status[r.id];
                if (!s) return <Text type="secondary">-</Text>;
                if (s.running) return <Tag color="processing">{t('Running')}</Tag>;
                return (
                    <Tooltip title={`${fmtTime(s.at)} · ${s.seconds || 0}s`}>
                        <Tag color={s.exit === 0 ? 'success' : 'error'}>{s.timed_out ? t('Timeout') : `exit ${s.exit}`}</Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: '', key: 'op', width: 72, render: (_, r) => (
                <Space size={0}>
                    <Tooltip title={t('Run now')}>
                        <Button size="small" type="text" icon={<PlayCircleOutlined />} disabled={!savedIds.has(r.id) || c.status !== 'running'} onClick={() => run(r.id)} />
                    </Tooltip>
                    <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => del(r.rid)} />
                </Space>
            ),
        },
    ];
    return (
        <Card size="small" title={t('Scheduled tasks')}>
            <Table size="small" rowKey="rid" columns={cols} dataSource={rows} pagination={false} scroll={{ x: 'max-content' }}
                locale={{ emptyText: t('No cron jobs') }}
                expandable={{
                    expandedRowRender: (r) => (
                        <Space wrap>
                            <Input size="small" addonBefore={t('Directory')} style={{ width: 280, ...mono }} value={r.workdir} placeholder="/"
                                onChange={(e) => set(r.rid, 'workdir', e.target.value)} />
                            <InputNumber size="small" addonBefore={t('Timeout')} addonAfter="s" min={0} max={86400} value={r.timeout || null}
                                placeholder="3600" onChange={(v) => set(r.rid, 'timeout', v || 0)} />
                        </Space>
                    ),
                }} />
            <Space style={{ marginTop: 8 }}>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => add({ schedule: '', command: '', enabled: true })}>{t('Add')}</Button>
                <Button size="small" type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
            </Space>
        </Card>
    );
};

// ------------------------------------------------------------------ 后台服务

export const ServicesCard = ({ c, reload }) => {
    const { t } = useTranslation();
    const [rows, set, add, del] = useRows(c.apps?.services);
    const [saving, setSaving] = useState(false);
    const status = c.apps_status?.services || {};

    const save = async () => {
        setSaving(true);
        const r = await api.apps(c.id, { cron: arr(c.apps?.cron), services: strip(rows) });
        setSaving(false);
        if (isOk(r)) { message.success(t('Saved')); reload(); } else message.error(errMsg(r, t('Failed to save')));
    };

    const cols = [
        { title: '', key: 'en', width: 56, render: (_, r) => <Switch size="small" checked={r.enabled !== false} onChange={(v) => set(r.rid, 'enabled', v)} /> },
        { title: t('Name'), key: 'n', width: 140, render: (_, r) => <Input size="small" value={r.name} placeholder="worker" onChange={(e) => set(r.rid, 'name', e.target.value.toLowerCase())} /> },
        { title: t('Command'), key: 'c', render: (_, r) => <Input size="small" style={mono} value={r.command} onChange={(e) => set(r.rid, 'command', e.target.value)} /> },
        {
            title: t('Status'), key: 'st', width: 120, render: (_, r) => {
                const s = status[r.name];
                if (!s) return <Text type="secondary">-</Text>;
                const tag = s.running ? <Tag color="success">{t('Running')}</Tag> : <Tag color="error">{t('Stopped')}</Tag>;
                const tip = [s.restarts ? `${t('Restart')} ${s.restarts}` : '', s.error || '', s.since ? fmtTime(s.since) : ''].filter(Boolean).join(' · ');
                return tip ? <Tooltip title={tip}>{tag}</Tooltip> : tag;
            },
        },
        { title: '', key: 'op', width: 40, render: (_, r) => <Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => del(r.rid)} /> },
    ];
    return (
        <Card size="small" title={<Tooltip title={t('Restarted automatically when it exits')}>{t('Background services')}</Tooltip>}>
            <Table size="small" rowKey="rid" columns={cols} dataSource={rows} pagination={false} scroll={{ x: 'max-content' }}
                locale={{ emptyText: t('No background services') }}
                expandable={{
                    expandedRowRender: (r) => (
                        <Input size="small" addonBefore={t('Directory')} style={{ width: 280, ...mono }} value={r.workdir} placeholder="/"
                            onChange={(e) => set(r.rid, 'workdir', e.target.value)} />
                    ),
                }} />
            <Space style={{ marginTop: 8 }}>
                <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => add({ name: '', command: '', enabled: true })}>{t('Add')}</Button>
                <Button size="small" type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
            </Space>
        </Card>
    );
};

// ------------------------------------------------------------------ 部署

const ARCHIVE_RE = /\.(tar\.gz|tgz|zip|tar)$/i;
const joinPath = (d, n) => (d.endsWith('/') ? d + n : `${d}/${n}`);
const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/** 从压缩包 URL 部署：下载 → 解压到目录 → 删除压缩包 → 重启容器（全部走文件通道，在容器内执行）。 */
export const DeployCard = ({ c, sockRef, ready }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [step, setStep] = useState('');
    const busy = !!step && step !== 'done';

    const waitJob = async (s, id) => {
        for (;;) {
            await sleep(1500);
            const d = await s.call('job_status', { job: id });
            const j = d.job || d;
            if (j.state === 'done') return;
            if (j.state === 'failed') throw new Error(j.error || t('Failed'));
        }
    };

    const deploy = async () => {
        const v = await form.validateFields();
        const url = v.url.trim();
        const dir = (v.dir || '/').trim();
        let ext;
        try { ext = (new URL(url).pathname.match(ARCHIVE_RE) || [])[1]; } catch (_) { ext = null; }
        if (!ext) { message.error(t('Only .zip / .tar.gz / .tgz / .tar are supported')); return; }
        const s = sockRef.current;
        if (!s || !ready) { message.error(t('Disconnected')); return; }
        const archive = joinPath(dir, `.deploy.${ext.toLowerCase()}`);
        try {
            setStep('fetch');
            const f = await s.call('job_start', { kind: 'fetch', url, path: archive });
            await waitJob(s, (f.job || {}).id);
            setStep('extract');
            const x = await s.call('job_start', { kind: 'extract', path: archive, dest: dir });
            await waitJob(s, (x.job || {}).id);
            await s.call('rm', { path: archive }).catch(() => {});
            setStep('restart');
            const r = await api.power(c.id, 'restart');
            if (!isOk(r)) throw new Error(errMsg(r, t('Restart failed')));
            setStep('done');
            message.success(t('Deployed'));
        } catch (e) {
            setStep('');
            message.error(e.message);
        }
    };

    const label = { fetch: t('Downloading…'), extract: t('Extracting…'), restart: t('Restarting…'), done: t('Deployed') }[step];
    return (
        <Card size="small" title={t('Deploy')}>
            <Form form={form} layout="inline" initialValues={{ dir: c.files_root || '/' }} style={{ rowGap: 8 }}>
                <Form.Item name="url" rules={[{ required: true, type: 'url', message: t('Enter a valid URL') }]}>
                    <Input placeholder={t('Archive URL (.zip / .tar.gz)')} style={{ width: 320 }} />
                </Form.Item>
                <Form.Item name="dir" rules={[{ required: true, pattern: /^\//, message: t('Enter an absolute path') }]}>
                    <Input addonBefore={t('Extract to')} style={{ width: 240, ...mono }} />
                </Form.Item>
                <Form.Item>
                    <Popconfirm title={t('Overwrite files with the same name and restart the container?')} onConfirm={deploy} disabled={busy || !ready}>
                        <Button type="primary" loading={busy} disabled={!ready}>{t('Deploy')}</Button>
                    </Popconfirm>
                </Form.Item>
                {label && <Form.Item><Text type={step === 'done' ? 'success' : 'secondary'}>{label}</Text></Form.Item>}
            </Form>
        </Card>
    );
};

// ------------------------------------------------------------------ PHP 设置

const PHP_FIELDS = [
    ['memory_limit', '128M'], ['upload_max_filesize', '2M'], ['post_max_size', '8M'],
    ['max_execution_time', '30'], ['max_input_vars', '1000'], ['date.timezone', 'UTC'],
];

export const PhpCard = ({ c, reload }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    // date.timezone 含点号：表单字段名用下划线
    const init = Object.fromEntries(Object.entries(c.php || {}).map(([k, v]) => [k.replace('.', '_'), v]));
    const save = async (v) => {
        const settings = Object.fromEntries(Object.entries(v).map(([k, val]) => [k === 'date_timezone' ? 'date.timezone' : k, (val ?? '').toString().trim()]));
        setSaving(true);
        const r = await api.php(c.id, settings);
        setSaving(false);
        if (isOk(r)) { message.success(t('Saved')); reload(); } else message.error(errMsg(r, t('Failed to save')));
    };
    return (
        <Card size="small" title={t('PHP settings')}>
            <Form form={form} layout="inline" initialValues={init} onFinish={save} style={{ rowGap: 8 }}>
                {PHP_FIELDS.map(([k, ph]) => (
                    <Form.Item key={k} name={k.replace('.', '_')} label={k}>
                        <Input size="small" placeholder={ph} style={{ width: 120, ...mono }} />
                    </Form.Item>
                ))}
                <Form.Item name="display_errors" label="display_errors">
                    <Select size="small" style={{ width: 100 }} options={[{ value: '', label: t('Default') }, { value: 'On', label: 'On' }, { value: 'Off', label: 'Off' }]} />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button size="small" type="primary" htmlType="submit" loading={saving}>{t('Save')}</Button>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t('The container restarts after saving')}</Text>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};
